import QuillEditor from "components/QuillEditor/QuillEditor";
import { BULLET_LIST, ORDERED_LIST } from "constants/richText";
import Quill, { Range } from "quill";
import LinkBlot from "quill/formats/link";
import { useEffect, useRef, useState } from "react";
import { parseRichText } from "util/richText";
import { RichTextLinkInput } from "./RichTextLinkInput";
import { RichTextToolbar } from "./RichTextToolbar";

type BareRichTextEditorProps = {
  defaultValue?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  hasLegacyMarkdown?: boolean;
};

type Formats = Record<string, unknown>;

const USER_SOURCE = "user";
const KEYUP_EVENT = "keyup";

const KEY_CODE_7 = 55;
const KEY_CODE_8 = 56;
const KEY_CODE_U = 85;

const EMPTY_FORMATS: Formats = {};

const EDITOR_CLASSES =
  "t-text-body t-text-text-100 [&_.ql-container]:!t-border-none [&_.ql-container]:![font:inherit] [&_.ql-editor]:![font:inherit] [&_.ql-editor]:!t-px-2 [&_.ql-editor]:!t-py-3 [&_.ql-editor]:!t-min-h-[60px] [&_.ql-blank::before]:!t-not-italic [&_.ql-blank::before]:!t-text-neutral-40 [&_.ql-blank::before]:!t-left-2 [&_.ql-blank::before]:!t-right-2 [&_.ql-tooltip]:!t-hidden [&_.ql-editor_a]:!t-text-purple";

const getActiveFormats = (quill: Quill): Formats => {
  const range = quill.getSelection();
  return range ? quill.getFormat(range) : EMPTY_FORMATS;
};

const toggleFormat = (
  quill: Quill,
  format: string,
  value: string | boolean
) => {
  const isActive = quill.getFormat()[format] === value;
  quill.format(format, isActive ? false : value, USER_SOURCE);
};

// With just a cursor inside a link, edit the whole link
const getLinkRange = (quill: Quill): Range => {
  const range = quill.getSelection(true);
  if (range.length > 0) return range;

  const [link, offset] = quill.scroll.descendant(LinkBlot, range.index);
  if (!link) return range;

  return { index: range.index - offset, length: link.length() };
};

export const BareRichTextEditor = ({
  defaultValue,
  onChange,
  placeholder,
  isDisabled = false,
  hasLegacyMarkdown = false,
}: BareRichTextEditorProps) => {
  const quillRef = useRef<Quill | null>(null);
  const [toolbarContainer] = useState(() => document.createElement("div"));
  const [formats, setFormats] = useState<Formats>(EMPTY_FORMATS);
  const [linkRange, setLinkRange] = useState<Range | null>(null);
  const [initialContent] = useState(() =>
    parseRichText(defaultValue, hasLegacyMarkdown)
  );

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const syncFormats = () => setFormats(getActiveFormats(quill));
    const toggleWithShortcut = (format: string, value: string) => () => {
      toggleFormat(quill, format, value);
      syncFormats();
    };

    quill.keyboard.addBinding(
      { key: KEY_CODE_7, shortKey: true, shiftKey: true },
      toggleWithShortcut("list", ORDERED_LIST)
    );
    quill.keyboard.addBinding(
      { key: KEY_CODE_8, shortKey: true, shiftKey: true },
      toggleWithShortcut("list", BULLET_LIST)
    );
    quill.keyboard.addBinding(
      { key: KEY_CODE_U, shortKey: true, shiftKey: true },
      () => setLinkRange(getLinkRange(quill))
    );

    quill.root.addEventListener(KEYUP_EVENT, syncFormats);
    return () => quill.root.removeEventListener(KEYUP_EVENT, syncFormats);
  }, []);

  const updateFormats = () => {
    const quill = quillRef.current;
    if (!quill) return;

    setFormats(getActiveFormats(quill));
  };

  const handleTextChange = () => {
    const quill = quillRef.current;
    if (!quill) return;

    const isEmpty = quill.getLength() <= 1;
    onChange(isEmpty ? "" : JSON.stringify(quill.getContents()));
    updateFormats();
  };

  const handleToggle = (format: string, value: string | boolean) => {
    const quill = quillRef.current;
    if (!quill) return;

    toggleFormat(quill, format, value);
    updateFormats();
  };

  const handleBold = () => handleToggle("bold", true);
  const handleItalic = () => handleToggle("italic", true);
  const handleNumberedList = () => handleToggle("list", ORDERED_LIST);
  const handleBulletedList = () => handleToggle("list", BULLET_LIST);

  const handleLink = () => {
    const quill = quillRef.current;
    if (!quill) return;

    setLinkRange(getLinkRange(quill));
  };

  const closeLinkInput = () => setLinkRange(null);

  const linkQuill = linkRange && !isDisabled ? quillRef.current : null;

  return (
    <div className="t-border t-border-solid t-border-neutral-10 t-rounded-md t-bg-white">
      <div className="t-p-1.5 t-bg-surface-lighter-grey t-rounded-t">
        <RichTextToolbar
          formats={formats}
          isDisabled={isDisabled}
          onBold={handleBold}
          onItalic={handleItalic}
          onLink={handleLink}
          onNumberedList={handleNumberedList}
          onBulletedList={handleBulletedList}
        />
      </div>
      {linkQuill && linkRange && (
        <div className="t-px-2 t-pt-3">
          <RichTextLinkInput
            quill={linkQuill}
            range={linkRange}
            onClose={closeLinkInput}
          />
        </div>
      )}
      <QuillEditor
        className={EDITOR_CLASSES}
        parentRef={quillRef}
        defaultValue={initialContent}
        onTextChange={handleTextChange}
        onSelectionChange={updateFormats}
        toolbar={toolbarContainer}
        placeholder={placeholder}
        disabled={isDisabled}
      />
    </div>
  );
};
